/** Booking dates are civil calendar days in America/Phoenix (YYYY-MM-DD). */

export const BOOKING_TIME_ZONE = 'America/Phoenix';

const CIVIL_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseCivilDateString(dateString) {
  if (!dateString || typeof dateString !== 'string') return null;
  const match = dateString.trim().match(CIVIL_DATE_RE);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const probe = new Date(year, month - 1, day);
  if (
    probe.getFullYear() !== year ||
    probe.getMonth() !== month - 1 ||
    probe.getDate() !== day
  ) {
    return null;
  }

  return { year, month, day, iso: `${match[1]}-${match[2]}-${match[3]}` };
}

/** Format a calendar Date to YYYY-MM-DD (the day the user selected on the picker). */
export function formatDateForApi(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Alias used by availability calendar code. */
export const formatDateLocal = formatDateForApi;

/** Parse stored YYYY-MM-DD without UTC day-shift. */
export function civilDateToLocalDate(dateString) {
  const civil = parseCivilDateString(dateString);
  if (!civil) return null;
  return new Date(civil.year, civil.month - 1, civil.day, 12, 0, 0);
}

/** Today's civil date in Arizona (YYYY-MM-DD). */
export function getTodayInBookingTimeZone() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BOOKING_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Whole days from one civil date to another (to - from). */
export function daysBetweenCivilDates(fromIso, toIso) {
  const from = civilDateToLocalDate(fromIso);
  const to = civilDateToLocalDate(toIso);
  if (!from || !to) return null;

  const fromUtc = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const toUtc = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((toUtc - fromUtc) / (24 * 60 * 60 * 1000));
}

/** Weekday name for a calendar Date or stored YYYY-MM-DD string. */
export function getBookingWeekday(dateOrString) {
  const date =
    typeof dateOrString === 'string'
      ? civilDateToLocalDate(dateOrString)
      : dateOrString;
  if (!date) return '';

  return date
    .toLocaleDateString('en-US', { timeZone: BOOKING_TIME_ZONE, weekday: 'long' })
    .toLowerCase();
}

/** Display a stored booking date string for UI. */
export function formatStoredBookingDate(dateString, options = {}) {
  const date = civilDateToLocalDate(dateString);
  if (!date) return '—';

  return date.toLocaleDateString('en-US', {
    timeZone: BOOKING_TIME_ZONE,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  });
}

/** Short display for admin lists (e.g. 7/4/2026). */
export function formatStoredBookingDateShort(dateString) {
  const date = civilDateToLocalDate(dateString);
  if (!date) return dateString || '—';

  return date.toLocaleDateString('en-US', {
    timeZone: BOOKING_TIME_ZONE,
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Format a calendar Date for customer-facing UI. */
export function formatCalendarDate(date, options = {}) {
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    timeZone: BOOKING_TIME_ZONE,
    ...options,
  });
}

/** Combine stored date + time for sorting/comparisons. */
export function bookingDateTimeFromParts(dateString, timeString) {
  const civil = parseCivilDateString(dateString);
  if (!civil) return null;

  const timeMatch = timeString?.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!timeMatch) return civilDateToLocalDate(dateString);

  let hour = parseInt(timeMatch[1], 10);
  const minute = parseInt(timeMatch[2], 10);
  const period = timeMatch[3].toUpperCase();

  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;

  return new Date(civil.year, civil.month - 1, civil.day, hour, minute);
}
